import { useState, useEffect, useCallback } from "react";
import { ContentCard } from "../content-card";
import ContentGrid from "./ContentGrid";
import ContentList from "./ContentList";
import NetworkView from "./NetworkView";
import ContentFrame from "./ContentFrame";
import FreeformView from "./FreeformView";
import { ContentItem } from "../content-card/types";
import { useHiddenTags } from "@/hooks/useHiddenTags";
import { useSearchFilter } from "@/hooks/useSearchFilter";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useParams } from "react-router-dom";
import { ViewMode } from "@/hooks/usePersistentViewMode";
import { useContentSelection } from "@/contexts/selection/SelectionContext";
import { useReWebClipContent } from "@/hooks/useReWebClipContent";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

interface ContentDisplayProps {
  contentItems: ContentItem[];
  viewMode: ViewMode;
  onContentClick: (id: string) => void;
  onDeleteContent: (id: string) => void;
  onEditContent?: (id: string, currentTitle: string) => void;
  onArchiveContent?: (id: string) => void;
  collectionColor?: string | null;
  storeNodePositionsInTag?: string;
}

// Export these filtering functions so they can be used by other components
export const useContentFilters = (contentItems: ContentItem[]) => {
  const { hiddenTags, isTagHidden } = useHiddenTags();
  const { id: currentRouteId } = useParams();
  const [hiddenTagsSnapshot, setHiddenTagsSnapshot] = useState(hiddenTags);

  // IMPROVED: Add a check if we're on a tag route
  const isOnTagRoute = window.location.pathname.includes('/tag/');

  const { data: contentTagRelations = [] } = useQuery({
    queryKey: ['contentTagRelations', JSON.stringify(hiddenTagsSnapshot)],
    queryFn: async () => {
      console.log("[TAG VISIBILITY] Fetching content tag relations with hiddenTags:", hiddenTagsSnapshot);
      const { data, error } = await supabase
        .from('content_tags')
        .select('content_id, tag_id');
      
      if (error) {
        console.error('Error fetching content tag relations:', error);
        return [];
      }
      
      return data;
    },
    staleTime: 5000,
  });

  useEffect(() => {
    setHiddenTagsSnapshot(hiddenTags);
  }, [hiddenTags]);

  const contentToTags = contentTagRelations.reduce((map: Record<string, string[]>, rel) => {
    if (!map[rel.content_id]) {
      map[rel.content_id] = [];
    }
    map[rel.content_id].push(rel.tag_id);
    return map;
  }, {});

  // IMPROVED: Handle tag filtering with special case for tag routes
  const tagFilteredItems = contentItems.filter(item => {
    const itemTags = contentToTags[item.id] || [];
    
    // If we're on a tag route and have a currentRouteId, prioritize showing items with that tag
    if (isOnTagRoute && currentRouteId) {
      // On a tag page, always include items that have the tag in the URL
      if (itemTags.includes(currentRouteId)) {
        console.log(`[TAG VISIBILITY] Item ${item.id} belongs to current tag ${currentRouteId}, including it`);
        return true;
      }
    }
    
    // For all other cases, apply hidden tags filter
    return !itemTags.some(tagId => isTagHidden(tagId));
  });

  // Apply search filter after tag filtering
  const searchFilteredItems = useSearchFilter(tagFilteredItems);

  console.log("[FILTERING DEBUG] Content filtering results:", {
    originalCount: contentItems.length,
    afterTagFiltering: tagFilteredItems.length,
    afterSearchFiltering: searchFilteredItems.length,
    hiddenTagsCount: Object.keys(hiddenTags).filter(key => hiddenTags[key]).length,
    currentRouteId,
    isTagRoute: isOnTagRoute,
    pageUrl: window.location.pathname
  });

  return {
    filteredItems: searchFilteredItems,
    tagFilteredItems,
    contentToTags
  };
};

const ContentDisplay = ({ 
  contentItems, 
  viewMode, 
  onContentClick, 
  onDeleteContent, 
  onEditContent,
  onArchiveContent,
  collectionColor,
  storeNodePositionsInTag
}: ContentDisplayProps) => {
  const { filteredItems: searchFilteredItems } = useContentFilters(contentItems);
  const { selectedIds, selectAll } = useContentSelection();
  const { batchReWebClipContent, batchStatus } = useReWebClipContent();
  const queryClient = useQueryClient();
  const { id: collectionId } = useParams();
  const { user } = useAuth();
  
  // Track navigation when component mounts or route changes
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    if (currentPath.includes('/collection/') && collectionId) {
      Tier1Tracking.trackNavigation(
        'collection',
        collectionId,
        undefined, // Collection name would need to be passed down
        contentItems.length,
        user
      );
    } else if (currentPath.includes('/tag/') && collectionId) {
      Tier1Tracking.trackNavigation(
        'tag',
        collectionId,
        undefined, // Tag name would need to be passed down
        contentItems.length,
        user
      );
    } else if (currentPath.includes('/board/') && collectionId) {
      Tier1Tracking.trackNavigation(
        'board',
        collectionId,
        undefined, // Board name would need to be passed down  
        contentItems.length,
        user
      );
    } else if (currentPath === '/dashboard' || currentPath === '/') {
      Tier1Tracking.trackNavigation(
        'inbox',
        undefined,
        'All Content',
        contentItems.length,
        user
      );
    }
  }, [collectionId, contentItems.length, user]);

  // Enhanced content click handler to track search result clicks
  const handleContentClick = (contentId: string) => {
    // Check if this click came from a search
    const searchTerm = localStorage.getItem('searchTerm');
    if (searchTerm && searchTerm.trim()) {
      Tier1Tracking.trackSearchResultClicked(searchTerm, contentId, user);
    }
    
    // Call the original handler
    onContentClick(contentId);
  };

  // Simplified hard refresh function that works reliably
  const performHardRefresh = useCallback(() => {
    console.log("[REFRESH] Executing hard page reload");
    toast.info("Hard refreshing page...");
    
    // Use setTimeout to ensure toast is visible
    setTimeout(() => {
      console.log("[REFRESH] Executing window.location.reload()");
      // Force a complete page reload, ignoring cache
      window.location.reload();
    }, 300);
    
    // Return a promise to maintain type compatibility
    return Promise.resolve();
  }, []);
  
  // Handle keyboard shortcuts with better event handling and clip level support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Select all with Cmd/Ctrl+A
      if ((e.metaKey || e.ctrlKey) && e.key === "a" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        e.stopPropagation();
        
        const ids = searchFilteredItems.map(item => item.id);
        selectAll(ids);
      }
      
      // Soft Re-clip selected items with Cmd/Ctrl+R (without modifiers)
      if ((e.metaKey || e.ctrlKey) && e.key === "r" && !e.altKey && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if there are selected items
        if (selectedIds.length === 0) {
          toast.info("No items selected for soft re-clipping. Select items first with Ctrl/Cmd+click or Shift+click.");
          return;
        }
        
        // Show initial toast with information
        toast.info(`Soft Re-Clipping ${selectedIds.length} items...`);
        
        // Execute batch soft re-clipping with direct reload on completion
        console.log(`[BATCH SOFT RE-CLIP] Initiating batch soft re-clip for ${selectedIds.length} items`);
        batchReWebClipContent(selectedIds, 'soft')
          .then(result => {
            console.log("[BATCH SOFT RE-CLIP] Completed with result:", result);
            toast.success(`Soft Re-Clipped ${result.successful} items successfully`);
            
            // Force immediate page reload after batch operation completes
            if (result.successful > 0) {
              toast.info("Refreshing page to show updated content...");
              setTimeout(() => {
                console.log("[BATCH SOFT RE-CLIP] Executing window.location.reload()");
                window.location.reload();
              }, 300);
            }
          })
          .catch(error => {
            console.error("[BATCH SOFT RE-CLIP] Error:", error);
            toast.error("Error during batch soft re-clipping");
          });
      }
      
      // Mid Re-clip selected items with Ctrl/Cmd+M
      if ((e.metaKey || e.ctrlKey) && e.key === "m" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if there are selected items
        if (selectedIds.length === 0) {
          toast.info("No items selected for mid re-clipping. Select items first with Ctrl/Cmd+click or Shift+click.");
          return;
        }
        
        // Show initial toast with information
        toast.info(`Mid Re-Clipping ${selectedIds.length} items...`);
        
        // Execute batch mid re-clipping with direct reload on completion
        console.log(`[BATCH MID RE-CLIP] Initiating batch mid re-clip for ${selectedIds.length} items`);
        batchReWebClipContent(selectedIds, 'mid')
          .then(result => {
            console.log("[BATCH MID RE-CLIP] Completed with result:", result);
            toast.success(`Mid Re-Clipped ${result.successful} items successfully`);
            
            // Force immediate page reload after batch operation completes
            if (result.successful > 0) {
              toast.info("Refreshing page to show updated content...");
              setTimeout(() => {
                console.log("[BATCH MID RE-CLIP] Executing window.location.reload()");
                window.location.reload();
              }, 300);
            }
          })
          .catch(error => {
            console.error("[BATCH MID RE-CLIP] Error:", error);
            toast.error("Error during batch mid re-clipping");
          });
      }
      
      // Hard refresh hotkey: Cmd/Ctrl+E - Direct reload with better handling
      if ((e.metaKey || e.ctrlKey) && e.key === "e" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        e.stopPropagation();
        
        console.log("[HOTKEY] Cmd/Ctrl+E pressed - forcing hard page reload");
        toast.info("Hard refreshing page...");
        
        // Brief delay to ensure the toast is shown
        setTimeout(() => {
          console.log("[HOTKEY] Executing window.location.reload()");
          window.location.reload();
        }, 300);
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [searchFilteredItems, selectAll, selectedIds, batchReWebClipContent]);
  
  // Show batch processing status
  useEffect(() => {
    if (batchStatus.inProgress) {
      document.title = `Re-Clipping (${batchStatus.processed}/${batchStatus.total}) - Clustr`;
    } else {
      // Reset title only if it was changed by this component
      if (document.title.includes("Re-Clipping")) {
        document.title = "Clustr";
      }
    }
    
    return () => {
      // Reset title on unmount if needed
      if (document.title.includes("Re-Clipping")) {
        document.title = "Clustr";
      }
    };
  }, [batchStatus]);
  
  console.log("[CONTENT DISPLAY] Rendering content with view mode:", viewMode, {
    originalItems: contentItems.length,
    filteredItems: searchFilteredItems.length,
    storeNodePositionsInTag,
    selectedItems: selectedIds.length,
    collectionId
  });

  // Ensure all items have a collection color if needed
  const finalItems = searchFilteredItems.map(item => {
    // Keep the item's own collection color if it has one
    if (item.collection_color) {
      return item;
    }
    
    // Otherwise use the provided collection color or default
    return {
      ...item,
      collection_color: collectionColor || "#6B7789"
    };
  });

  // For network and freeform views, we don't need selection
  if (viewMode === "freeform") {
    return (
      <FreeformView
        contentItems={finalItems}
        onContentClick={handleContentClick}
        onDeleteContent={onDeleteContent}
        onEditContent={onEditContent}
        onArchiveContent={onArchiveContent}
        collectionColor={collectionColor}
      />
    );
  }

  if (viewMode === "network") {
    return (
      <NetworkView
        contentItems={finalItems}
        collectionColor={collectionColor}
      />
    );
  }

  if (viewMode === "frame") {
    return (
      <ContentFrame
        contentItems={finalItems}
        onContentClick={handleContentClick}
        onDeleteContent={onDeleteContent}
        onEditContent={onEditContent}
        onArchiveContent={onArchiveContent}
        collectionColor={collectionColor}
      />
    );
  }

  // Get all content IDs for range selection
  const allContentIds = finalItems.map(item => item.id);

  return viewMode === "grid" ? (
    <ContentGrid
      contentItems={finalItems}
      onContentClick={handleContentClick}
      onDeleteContent={onDeleteContent}
      onEditContent={onEditContent}
      onArchiveContent={onArchiveContent}
      collectionColor={collectionColor}
      allContentIds={allContentIds}
    />
  ) : (
    <ContentList
      contentItems={finalItems}
      onContentClick={handleContentClick}
      onDeleteContent={onDeleteContent}
      onEditContent={onEditContent}
      onArchiveContent={onArchiveContent}
      collectionColor={collectionColor}
      allContentIds={allContentIds}
    />
  );
};

export default ContentDisplay;
